import React from 'react';
import { Formik, FormikProps } from 'formik';
import { Form } from 'formik-antd';
import * as Yup from 'yup';

import FormikEffect from 'components/formik/FormikEffect';
import WorkerSection, {
  WorkerFormValues,
  workerInitialValidationSchema,
  workerInitialValues,
} from 'components/modals/AssignmentDataModal/WorkerSection';
import { WorkerDetailViewModel } from 'api/backofficeApiClient';
import { checkIsCountriesWithStatesDropdown } from 'utils/validators/countryUtils';
import { Address, Addresses, FormType } from '../../AssignmentModal';

type Props = {
  formRef: any;
  workers: WorkerDetailViewModel[];
  onChange: (formik: FormikProps<WorkerInfoFormValues>) => void;
  initialValues?: WorkerInfoFormValues;
  isWorkersLoaded: boolean;
  isRenewOrEditRenewMode: boolean;
  isRehireOrEditRehireMode: boolean;
  hasActiveAssignmentRestriction: boolean;
  addresses: Addresses;
  onAddressChange: (
    formType: FormType,
    field: keyof Address,
    value: string | undefined,
  ) => void;
};

export interface WorkerInfoFormValues {
  worker: WorkerFormValues;
}

const initialValidationSchema = {
  worker: workerInitialValidationSchema,
};

const getSubmitValidationSchema = (addresses: Addresses) =>
  Yup.object({
    worker: Yup.object({
      ...initialValidationSchema.worker,
      workerId:
        initialValidationSchema.worker.workerId.required('Field is required'),
      firstName:
        initialValidationSchema.worker.firstName.required('Field is required'),
      lastName:
        initialValidationSchema.worker.lastName.required('Field is required'),
      email: initialValidationSchema.worker.email.required('Field is required'),
      phone: initialValidationSchema.worker.phone.required('Field is required'),
      workerType:
        initialValidationSchema.worker.workerType.required('Field is required'),
      country:
        initialValidationSchema.worker.country.required('Field is required'),
      state: initialValidationSchema.worker.state.when('country', {
        is: (country: number) => checkIsCountriesWithStatesDropdown(country),
        then: Yup.string().required('Field is required'),
      }),
      city: initialValidationSchema.worker.city.when('country', {
        is: (country: number) => checkIsCountriesWithStatesDropdown(country),
        then: Yup.string().required('Field is required'),
      }),
      internationalStateName:
        initialValidationSchema.worker.internationalStateName.when('country', {
          is: (country: number) => !checkIsCountriesWithStatesDropdown(country),
          then: Yup.string().required('Field is required'),
        }),
      internationalCityName:
        initialValidationSchema.worker.internationalCityName.when('country', {
          is: (country: number) => !checkIsCountriesWithStatesDropdown(country),
          then: Yup.string().required('Field is required'),
        }),
      address: initialValidationSchema.worker.address
        .required('Field is required')
        .billingAndWorkerAddressSameCheck(
          addresses.worker.address,
          addresses.billing.address,
        ),
    }),
  });

export default React.memo((props: Props) => {
  const {
    formRef,
    onChange,
    initialValues: predefinedInitialValues,
    workers,
    isWorkersLoaded,
    isRenewOrEditRenewMode,
    isRehireOrEditRehireMode,
    hasActiveAssignmentRestriction,
    addresses,
    onAddressChange,
  } = props;
  const initialValues: WorkerInfoFormValues = {
    worker: workerInitialValues,
  };

  return (
    <Formik
      innerRef={formRef}
      enableReinitialize
      initialValues={predefinedInitialValues || initialValues}
      validationSchema={getSubmitValidationSchema(addresses)}
      onSubmit={() => {}}
    >
      {(formik) => (
        <>
          <FormikEffect onChange={onChange} />

          <Form layout="vertical">
            <div className="modal-scroll-wrapper">
              <WorkerSection<WorkerInfoFormValues>
                formik={formik}
                workers={workers}
                isWorkersLoaded={isWorkersLoaded}
                predefinedInitialValues={predefinedInitialValues}
                isRenewOrEditRenewMode={isRenewOrEditRenewMode}
                isRehireOrEditRehireMode={isRehireOrEditRehireMode}
                hasActiveAssignmentRestriction={hasActiveAssignmentRestriction}
                addresses={addresses}
                onAddressChange={onAddressChange}
              />
            </div>
          </Form>
        </>
      )}
    </Formik>
  );
});
